import { processApiFieldErrors } from '../../../../utils/processApiFieldErrors'
// getErrorMessage lives in utils.js; TS imports it fine (allowJs: true).
import { getErrorMessage } from '../../../../utils/utils'
import type { TeamPlayerFinderRegistration } from '../types'

type ApiErrorShape = {
  status?: number
  statusCode?: number
  message?: string
  invalid_fields?: Record<string, string | string[]>
  data?: { registrations?: unknown }
}

/** The HTTP status req() attached to a thrown error, or null. */
export const playerFinderErrorStatus = (error: unknown): number | null => {
  const e = error as ApiErrorShape | null | undefined

  return e?.status ?? e?.statusCode ?? null
}

/** The server refused a save made from a stale revision. */
export const isPlayerFinderConflict = (error: unknown): boolean =>
  playerFinderErrorStatus(error) === 409

/**
 * A 409's body carries the server's FRESH settings (req() throws a 4xx as the
 * parsed body, so they sit under `data.registrations`). Null for any other error
 * or a body without them.
 */
export const freshRegistrationsFromConflict = (
  error: unknown
): TeamPlayerFinderRegistration[] | null => {
  if (!isPlayerFinderConflict(error)) {
    return null
  }
  const registrations = (error as ApiErrorShape).data?.registrations

  return Array.isArray(registrations)
    ? (registrations as TeamPlayerFinderRegistration[])
    : null
}

/** API field → the settings card's Formik field. */
export const PLAYER_FINDER_BE_TO_FORM_FIELD: Record<string, string> = {
  recruiting: 'recruiting',
  positions: 'positionIds',
}

export const PLAYER_FINDER_BE_TO_FIELD_TITLE: Record<string, string> = {
  recruiting: 'Looking for players',
  positions: 'Positions needed',
}

/** `positions.2` (one bad id) reports against the positions field as a whole. */
const collapseIndexedKeys = (
  invalidFields: Record<string, string | string[]> | undefined
): Record<string, string | string[]> | undefined => {
  if (!invalidFields) {
    return invalidFields
  }

  return Object.entries(invalidFields).reduce<
    Record<string, string | string[]>
  >((fields, [key, message]) => {
    const field = key.replace(/\.\d+$/, '')
    if (!(field in fields)) {
      fields[field] = message
    }

    return fields
  }, {})
}

export interface ApplyPlayerFinderApiErrorDeps {
  /** Formik `setErrors`. */
  setErrors: (errors: Record<string, string>) => void
  /** Formik `setTouched` — web gates field errors on touched. */
  setTouched?: (touched: Record<string, boolean>) => void
  /** Surface a message that belongs to no field (web: message.error). */
  notifyError: (message: string) => void
}

/**
 * Translate a refused save (422 / 403 / 404) into the card's field errors, or a
 * message when nothing maps to a field. A 409 is NOT handled here: it is not a
 * mistake in the form but a change underneath it — see
 * `freshRegistrationsFromConflict`.
 */
export const applyPlayerFinderApiError = (
  err: unknown,
  deps: ApplyPlayerFinderApiErrorDeps
): void => {
  const { setErrors, setTouched, notifyError } = deps
  const e = (err ?? {}) as ApiErrorShape

  const { formErrors, titles, hasFieldErrors } = processApiFieldErrors(
    collapseIndexedKeys(e.invalid_fields),
    {
      fieldMap: PLAYER_FINDER_BE_TO_FORM_FIELD,
      titleMap: PLAYER_FINDER_BE_TO_FIELD_TITLE,
    }
  )

  if (!hasFieldErrors || titles.length === 0) {
    notifyError(e.message || getErrorMessage(err))
    return
  }

  setErrors(formErrors)
  setTouched?.(
    Object.keys(formErrors).reduce<Record<string, boolean>>((touched, key) => {
      touched[key] = true
      return touched
    }, {})
  )
}
